/**
 * SOP terminal delivery semantics: submit vs save-only (logged only, no delivery rules).
 * Pure helpers — no I/O.
 */

export function parseStepConfigJson(raw: unknown): Record<string, unknown> | null {
  if (raw == null) return null;
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const o = JSON.parse(raw) as unknown;
      return typeof o === 'object' && o !== null && !Array.isArray(o) ? (o as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }
  return null;
}

/** True when this TERMINAL step should suppress scenario.submitted / incident.completed style delivery. */
export function isSaveOnlyTerminalStep(step: {
  archetype?: string | null;
  variant?: string | null;
  config?: unknown;
}): boolean {
  const arch = String(step?.archetype || '').toUpperCase();
  if (arch !== 'TERMINAL') return false;
  const v = String(step?.variant || '').trim().toLowerCase();
  if (v === 'save_only') return true;
  const cfg = parseStepConfigJson(step?.config);
  const co = cfg?.terminalOutcome;
  if (typeof co === 'string' && co.trim().toLowerCase() === 'save_only') return true;
  if (cfg?.deliverySuppressed === true || cfg?.deliverySuppressed === 1) return true;
  return false;
}

export function isSubmitTerminalStep(step: {
  archetype?: string | null;
  variant?: string | null;
  config?: unknown;
  stepType?: string | null;
}): boolean {
  const raw = String(step?.archetype || step?.stepType || '').trim().toUpperCase();
  if (raw !== 'TERMINAL' && raw !== 'END_SOP') return false;
  return !isSaveOnlyTerminalStep({
    archetype: 'TERMINAL',
    variant: step?.variant,
    config: step?.config,
  });
}

/** True when execution log contains a completed TERMINAL step (modern SOP closure signal). */
export function hasCompletedTerminalStep(executionLog: unknown): boolean {
  if (!Array.isArray(executionLog)) return false;
  return executionLog.some((step: any) => {
    if (String(step?.stepType || '').toUpperCase() !== 'TERMINAL') return false;
    const action = String(step?.action || '').toLowerCase();
    return action === 'complete';
  });
}

export type SopStepMeta = {
  id: number | string;
  archetype: string | null;
  variant: string | null;
  config: unknown;
};

/**
 * From execution log + step metadata, determine if the final completed terminal was save-only.
 * Uses the last log entry that references a known TERMINAL step (by stepId).
 */
export function deriveDeliverySuppressedFromExecutionLog(
  executionLog: unknown,
  stepRows: SopStepMeta[],
): boolean {
  if (!Array.isArray(executionLog) || executionLog.length === 0) return false;
  const metaById = new Map<string, SopStepMeta>();
  for (const r of stepRows) {
    metaById.set(String(r.id), r);
  }
  let lastTerminal: SopStepMeta | null = null;
  for (const entry of executionLog as any[]) {
    const sid = entry?.stepId ?? entry?.step_id;
    if (sid == null) continue;
    const meta = metaById.get(String(sid));
    if (!meta) continue;
    if (String(meta.archetype || '').toUpperCase() === 'TERMINAL') {
      lastTerminal = meta;
    }
  }
  if (!lastTerminal) return false;
  return isSaveOnlyTerminalStep(lastTerminal);
}
